import { cs } from '../../../lib/currency';
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import {
  CreditCard, AlertCircle, Loader2, Banknote,
} from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { extractErrorMessage } from './utils.jsx';

/**
 * Charge a saved card / bank account against the selected invoice.
 * Owns its own dialog-scoped state (payment methods, surcharge override)
 * so the parent only has to provide the invoice and an `onCharged` hook.
 */
export default function ChargeCardDialog({ open, onOpenChange, invoice, onCharged }) {
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [globalSurcharge, setGlobalSurcharge] = useState({ enabled: true, percent: 3.0 });
  const [surchargeOverrideEnabled, setSurchargeOverrideEnabled] = useState(null);
  const [surchargePercentOverride, setSurchargePercentOverride] = useState('');
  const [processing, setProcessing] = useState(false);
  // Account credit: when the parent has credit, admins choose (per invoice)
  // whether to apply it and exactly how much, then only charge the balance.
  // Credit comes from two separate pots the app tracks independently:
  // `account` (customer_credits pool) and `enrollment` (per-entity residuals).
  const [creditAccount, setCreditAccount] = useState(0);
  const [creditEnrollment, setCreditEnrollment] = useState(0);
  const [applyCredit, setApplyCredit] = useState(true);
  const [creditInput, setCreditInput] = useState('');

  // iter270: for PARTIAL invoices the charge target is the outstanding
  // balance_due, not the full invoice amount.
  const chargeBase = invoice
    ? (invoice.status === 'partial' && invoice.balance_due != null
        ? Number(invoice.balance_due)
        : Number(invoice.amount || 0))
    : 0;
  const totalCredit = Math.round((Number(creditAccount || 0) + Number(creditEnrollment || 0)) * 100) / 100;
  // Never apply more credit than the invoice needs.
  const maxApplicable = Math.round(Math.min(totalCredit, chargeBase) * 100) / 100;
  const creditToApply = applyCredit
    ? Math.round(Math.min(Math.max(0, Number(creditInput) || 0), maxApplicable) * 100) / 100
    : 0;
  const cardAmount = Math.max(0, Math.round((chargeBase - creditToApply) * 100) / 100);
  const creditCoversFull = creditToApply >= chargeBase - 0.005 && chargeBase > 0;

  // Keep the editable credit amount in sync once balances/invoice load.
  useEffect(() => {
    setCreditInput(maxApplicable > 0 ? String(maxApplicable) : '');
  }, [maxApplicable]);

  useEffect(() => {
    if (!open || !invoice) return;
    setPaymentMethods(null);
    setSelectedPaymentMethodId(null);
    setSurchargeOverrideEnabled(null);
    setSurchargePercentOverride('');
    setCreditAccount(0);
    setCreditEnrollment(0);
    setApplyCredit(true);

    let cancelled = false;
    (async () => {
      // iter389 — fetch saved payment methods FIRST, with a bounded
      // timeout. Previously this ran after two un-bounded awaits
      // (billing-settings, billing-info); if either hung, the PM fetch
      // never fired and the dialog spun "Loading payment methods…"
      // forever. Now the PM list resolves (or falls back to "no method")
      // independently of the slower credit/settings lookups.
      try {
        const res = await api.get(
          `/admin/parent/${invoice.parent_id}/payment-methods`,
          { timeout: 20000 },
        );
        if (!cancelled) {
          setPaymentMethods(res.data);
          if (res.data?.payment_methods?.length > 0) {
            setSelectedPaymentMethodId(res.data.payment_methods[0].id);
          }
        }
      } catch {
        if (!cancelled) setPaymentMethods({ has_payment_method: false, payment_methods: [] });
      }
      try {
        const { data } = await api.get('/admin/billing-settings', { timeout: 20000 });
        if (!cancelled) {
          setGlobalSurcharge({
            enabled: data?.cc_surcharge_enabled ?? true,
            percent: Number(data?.cc_surcharge_percent ?? 3.0),
          });
        }
      } catch {
        // non-fatal — keep defaults
      }
      try {
        const { data: bi } = await api.get(`/parents/${invoice.parent_id}/credit-balance`, { timeout: 20000 });
        if (!cancelled) {
          setCreditAccount(Number(bi?.customer_credit_balance || 0));
          setCreditEnrollment(Number(bi?.credit_balance || 0));
        }
      } catch {
        if (!cancelled) { setCreditAccount(0); setCreditEnrollment(0); }
      }
    })();
    return () => { cancelled = true; };
  }, [open, invoice]);

  const handleCharge = async () => {
    if (!invoice) return;
    setProcessing(true);
    try {
      // Step 1 — apply account credit first (admin opted in). This marks
      // the invoice PARTIAL (or PAID if credit covers it fully) and drains
      // only the available credit — never more.
      if (creditToApply > 0.005) {
        await api.post('/admin/invoices/mark-paid', {
          invoice_id: invoice.id,
          invoice_type: invoice.type,
          monthly_invoice_id: invoice.monthly_invoice_id || undefined,
          payment_method: 'account_credit',
          amount: creditToApply,
        });
        if (creditCoversFull) {
          toast.success(`Applied ${cs()}${creditToApply.toFixed(2)} account credit — invoice fully paid`);
          onOpenChange(false);
          onCharged && onCharged();
          return;
        }
      }
      // Step 2 — charge the remaining balance to the card/bank. The backend
      // re-reads the (now partial) invoice and bills only balance_due.
      const { data } = await api.post('/admin/invoices/charge', {
        invoice_id: invoice.id,
        invoice_type: invoice.type,
        // iter307df — Forward the row's monthly_invoice_id so the backend
        // bills the exact MI the admin clicked (not a sibling paid/older
        // MI on the same enrollment). Without this, single-MI rows
        // identified by enrollment_id can silently charge the wrong
        // amount when paid + unpaid MIs coexist.
        monthly_invoice_id: invoice.monthly_invoice_id || undefined,
        payment_method_id: selectedPaymentMethodId || undefined,
        cc_surcharge_override:
          surchargeOverrideEnabled === null ? undefined : surchargeOverrideEnabled,
        cc_surcharge_percent_override:
          surchargePercentOverride === '' ? undefined : Number(surchargePercentOverride),
      });
      const creditNote = creditToApply > 0.005 ? ` (after ${cs()}${creditToApply.toFixed(2)} credit)` : '';
      if (data?.payment_status === 'processing') {
        toast.success(data.message || `Bank transfer for ${cs()}${cardAmount.toFixed(2)} initiated${creditNote}. Will settle in 3–5 business days.`, { duration: 8000 });
      } else {
        toast.success(data?.message || `Successfully charged ${cs()}${cardAmount.toFixed(2)}${creditNote}`);
      }
      onOpenChange(false);
      onCharged && onCharged();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to charge card'));
    } finally {
      setProcessing(false);
    }
  };

  const selectedIsCard = paymentMethods?.has_payment_method &&
    selectedPaymentMethodId &&
    paymentMethods.payment_methods.find((pm) => pm.id === selectedPaymentMethodId)?.type === 'card';

  const willAddSurcharge = surchargeOverrideEnabled === true ||
    (surchargeOverrideEnabled === null && globalSurcharge.enabled);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-600 shrink-0" />
            Charge Parent's Card
          </DialogTitle>
          <DialogDescription>
            Run a Stripe charge against this invoice using the parent's saved
            card or bank account. ACH transfers settle in 3–5 business days.
          </DialogDescription>
        </DialogHeader>
        {invoice && (
          <div className="space-y-4 min-w-0">
            <div className="bg-slate-50 p-4 rounded-lg space-y-2 min-w-0 break-words">
              <p className="break-words"><strong>Invoice:</strong> {invoice.description}</p>
              <p className="break-words"><strong>Parent:</strong> {invoice.parent_name}</p>
              <p className="break-words"><strong>Email:</strong> {invoice.parent_email}</p>
              {invoice.player_names && (
                <p className="break-words"><strong>Player(s):</strong> {invoice.player_names}</p>
              )}
              <p className="text-lg font-semibold text-green-600">
                <strong>Amount:</strong> {cs()}{chargeBase.toFixed(2)}
                {invoice.status === 'partial' && (
                  <span className="block text-xs font-normal text-amber-700 mt-0.5">
                    (Invoice total {cs()}{Number(invoice.amount || 0).toFixed(2)} · credit applied {cs()}{Number(invoice.credit_applied || 0).toFixed(2)})
                  </span>
                )}
              </p>
            </div>

            {totalCredit > 0.005 && chargeBase > 0 && (
              <div className="border rounded-lg p-4 space-y-3 bg-emerald-50/60 border-emerald-200" data-testid="charge-credit-prompt">
                <div>
                  <Label className="font-medium block">
                    Account credit available: {cs()}{totalCredit.toFixed(2)}
                  </Label>
                  {(creditAccount > 0.005 && creditEnrollment > 0.005) && (
                    <p className="text-xs text-slate-600 mt-0.5">
                      {cs()}{creditAccount.toFixed(2)} account credit + {cs()}{creditEnrollment.toFixed(2)} enrollment credit
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2 text-sm">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="apply-credit"
                      checked={applyCredit}
                      onChange={() => setApplyCredit(true)}
                      className="mt-1"
                      data-testid="apply-credit-yes"
                    />
                    <span>Apply account credit to this invoice</span>
                  </label>
                  {applyCredit && (
                    <div className="ml-6 flex items-center gap-2" data-testid="credit-amount-row">
                      <span className="text-slate-600">Apply $</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        max={maxApplicable}
                        value={creditInput}
                        onChange={(e) => setCreditInput(e.target.value)}
                        className="w-24 border border-slate-300 rounded px-2 py-1 text-sm"
                        data-testid="credit-amount-input"
                      />
                      <span className="text-slate-600">
                        {creditToApply >= chargeBase - 0.005
                          ? '— covers the full invoice'
                          : <>— then charge <strong>{cs()}{cardAmount.toFixed(2)}</strong> to the payment method</>}
                      </span>
                    </div>
                  )}
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="apply-credit"
                      checked={!applyCredit}
                      onChange={() => setApplyCredit(false)}
                      className="mt-1"
                      data-testid="apply-credit-no"
                    />
                    <span>Don&apos;t use credit — charge the full <strong>{cs()}{chargeBase.toFixed(2)}</strong></span>
                  </label>
                </div>
              </div>
            )}

            <div className="border rounded-lg p-4">
              <Label className="font-medium mb-2 block">Payment Method</Label>
              {paymentMethods === null ? (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading payment methods...
                </div>
              ) : paymentMethods.has_payment_method ? (
                <div className="space-y-2">
                  {paymentMethods.payment_methods.map((pm) => {
                    const isBank = pm.type === 'us_bank_account';
                    const checked = selectedPaymentMethodId === pm.id;
                    return (
                      <label
                        key={pm.id}
                        className={`flex items-center gap-3 p-2 rounded border cursor-pointer transition ${checked ? 'bg-blue-50 border-blue-400 ring-1 ring-blue-200' : 'bg-white border-slate-200 hover:border-slate-300'}`}
                        data-testid={`charge-pm-option-${pm.id}`}
                      >
                        <input
                          type="radio"
                          name="charge-pm"
                          value={pm.id}
                          checked={checked}
                          onChange={() => setSelectedPaymentMethodId(pm.id)}
                          className="h-4 w-4"
                        />
                        {isBank ? <Banknote className="h-5 w-5 text-indigo-600" /> : <CreditCard className="h-5 w-5 text-green-600" />}
                        <div className="flex-1 min-w-0">
                          {isBank ? (
                            <>
                              <p className="font-medium text-sm truncate">{pm.bank_name || 'Bank'} · {(pm.account_type || 'account').toString().replace('_', ' ')}</p>
                              <p className="text-xs text-slate-500">Account ending {pm.last4}</p>
                            </>
                          ) : (
                            <>
                              <p className="font-medium text-sm capitalize">{pm.brand} ending {pm.last4}</p>
                              <p className="text-xs text-slate-500">Expires {pm.exp_month}/{pm.exp_year}</p>
                            </>
                          )}
                        </div>
                        {isBank && (
                          <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200 shrink-0">
                            ACH · 3–5d
                          </Badge>
                        )}
                      </label>
                    );
                  })}
                  {paymentMethods.payment_methods.some((pm) => pm.type === 'us_bank_account') && selectedPaymentMethodId &&
                    paymentMethods.payment_methods.find((pm) => pm.id === selectedPaymentMethodId)?.type === 'us_bank_account' && (
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                        Bank transfers (ACH) settle in 3–5 business days. The invoice will auto-mark as paid once Stripe confirms the charge.
                      </div>
                    )}
                  {paymentMethods.stripe_mode && (
                    <p className="text-[10px] text-slate-400 mt-2">Stripe mode: {paymentMethods.stripe_mode.toUpperCase()}{paymentMethods.relinked_via_email_fallback ? ' · re-linked via email' : ''}</p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded">
                    <AlertCircle className="h-5 w-5" />
                    <span>No saved payment method found for this parent</span>
                  </div>
                  <div className="text-xs text-slate-500 space-y-1">
                    {paymentMethods.stripe_mode && <p>Stripe mode: <span className="font-medium uppercase">{paymentMethods.stripe_mode}</span> {paymentMethods.stripe_mode === 'test' ? '— this server is in TEST mode; live-mode saved cards are invisible here.' : ''}</p>}
                    {paymentMethods.fallback_checked && <p>Also checked by email — no Stripe customer with a saved method found.</p>}
                    <p>Ask the parent to complete any Stripe checkout (Book Lane / Tournament) — their card will auto-save here for future admin-billed charges.</p>
                  </div>
                </div>
              )}
            </div>

            {selectedIsCard && (
              <div className="border rounded-lg p-4 space-y-2" data-testid="cc-surcharge-controls">
                <div className="flex items-center justify-between">
                  <Label className="font-medium">Credit card surcharge</Label>
                  <span className="text-[11px] text-slate-500">
                    Global default: {globalSurcharge.enabled ? `${globalSurcharge.percent}% ON` : 'OFF'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      name="surcharge-mode"
                      checked={surchargeOverrideEnabled === null}
                      onChange={() => { setSurchargeOverrideEnabled(null); setSurchargePercentOverride(''); }}
                      data-testid="surcharge-use-global"
                    />
                    Use global
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      name="surcharge-mode"
                      checked={surchargeOverrideEnabled === true}
                      onChange={() => setSurchargeOverrideEnabled(true)}
                      data-testid="surcharge-force-on"
                    />
                    Add surcharge
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      name="surcharge-mode"
                      checked={surchargeOverrideEnabled === false}
                      onChange={() => { setSurchargeOverrideEnabled(false); setSurchargePercentOverride(''); }}
                      data-testid="surcharge-force-off"
                    />
                    Skip surcharge
                  </label>
                </div>
                {(surchargeOverrideEnabled === true ||
                  (surchargeOverrideEnabled === null && globalSurcharge.enabled)) && (
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500 w-24">Override % (optional)</Label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="10"
                      placeholder={`${globalSurcharge.percent}`}
                      value={surchargePercentOverride}
                      onChange={(e) => setSurchargePercentOverride(e.target.value)}
                      className="h-8 w-24 border rounded px-2 text-sm"
                      data-testid="surcharge-percent-override"
                    />
                    <span className="text-xs text-slate-500">%</span>
                  </div>
                )}
                {willAddSurcharge && (() => {
                  const base = cardAmount;
                  if (base <= 0) return null;
                  const pct = surchargePercentOverride !== ''
                    ? Number(surchargePercentOverride)
                    : globalSurcharge.percent;
                  const surcharge = Math.round(base * (pct / 100) * 100) / 100;
                  return (
                    <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-800">
                      Charging <strong>{cs()}{(base + surcharge).toFixed(2)}</strong> = {cs()}{base.toFixed(2)} + {cs()}{surcharge.toFixed(2)} ({pct}% surcharge)
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleCharge}
            disabled={processing || (!creditCoversFull && !paymentMethods?.has_payment_method)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="charge-submit-button"
          >
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : creditCoversFull ? (
              <>
                <CreditCard className="h-4 w-4 mr-2" />
                Apply {cs()}{creditToApply.toFixed(2)} credit
              </>
            ) : (
              <>
                <CreditCard className="h-4 w-4 mr-2" />
                {creditToApply > 0.005
                  ? `Apply ${cs()}${creditToApply.toFixed(2)} + charge ${cs()}${cardAmount.toFixed(2)}`
                  : `Charge ${cs()}${cardAmount.toFixed(2)}`}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
